'use client';

import { Landscape_Description } from '@/components/ui/catalog/landscape/description';
import { Landscape_Display } from '../landscape/LandscapeDisplay';
import { Landscape_Immersive } from '../landscape/LandscapeImmersive';
import { Landscape_Vision_Display } from '../landscape/LandscapeVisionDisplay';
import { Landscape_ChapterHero } from '../landscape/LandscapeChapterHero';
import { Landscape_Chapter } from '../landscape/LandscapeChapter';
import { Landscape_Process } from '../landscape/LandscapeProcess';
import { Landscape_Sketchbook } from '../landscape/LandscapeSketchbook';
import { features } from '@/lib/config/features';
import type { Nftcollection } from '@/types';

/**
 * Renders a collection's layout blocks, shared between the /info page and the
 * inline "read about the series" section on the collection page itself. The
 * specs table (edition, chain, contract, platform) is derived from the record
 * and attached to the first LandscapeDescription block, exactly as the info
 * page has always done.
 */

type Block = NonNullable<Nftcollection['layout']>[number];

export interface CollectionWithExtras extends Nftcollection {
  tags?: string[];
  cats?: string[];
}

function contractKey(record: CollectionWithExtras) {
  const { blockchain: chain = '', contractAddress: address = '' } = record.requiredInformation ?? {};
  const normalized = ['ethereum', 'polygon', 'base'].includes(chain) ? address.toLowerCase() : address;
  return `${chain}:${normalized}`;
}

export function buildCollectionSpecs(detail: CollectionWithExtras, relatedCollections: CollectionWithExtras[] = []) {
  const specs: Array<{ label: string; specifications: Array<{ value: string; url?: string; light?: boolean }> }> = [];

  if (detail.collectionTypes && detail.collectionTypes.length > 0) {
    specs.push({
      label: 'ARTWORK TYPE',
      specifications: detail.collectionTypes.map(collectionType => ({
        value: typeof collectionType === 'string' ? collectionType : String((collectionType as { title?: string })?.title ?? collectionType),
      })),
    });
  }
  if (detail.cats && detail.cats.length > 0) {
    specs.push({
      label: 'CATEGORIES',
      specifications: detail.cats.map(cat => ({ value: cat })),
    });
  }
  if (detail.totalSupply != null) {
    specs.push({ label: 'EDITION', specifications: [{ value: `${detail.totalSupply}/1/1`, light: true }] });
  }
  if (detail.requiredInformation?.blockchain) {
    specs.push({
      label: 'CHAIN',
      specifications: [{ value: detail.requiredInformation.blockchain.toUpperCase() }],
    });
    if (features.store) {
      specs.push({
        label: 'PRICE',
        specifications: [
          { value: `${detail.publicPrice} ${detail.requiredInformation.blockchain.toUpperCase()}`, light: true },
        ],
      });
    }
  }
  if (detail.tokenStandard && typeof detail.tokenStandard !== 'number') {
    specs.push({
      label: 'Token',
      specifications: [{ value: detail.tokenStandard.title }],
    });
  }
  const records = [detail, ...relatedCollections.filter(record => record.id !== detail.id)];
  const contracts = new Map<string, { address: string; chain: string; names: string[] }>();
  for (const record of records) {
    const { contractAddress: address, blockchain: chain = '' } = record.requiredInformation ?? {};
    if (!address) continue;
    const key = contractKey(record);
    const contract = contracts.get(key);
    if (contract) {
      // An umbrella record can repeat a child's contract: label that contract
      // with its actual issuing collection rather than the umbrella series.
      if (record.id !== detail.id) { contract.names = contract.names.filter(name => name !== detail.name); contract.address = address; }
      if (!contract.names.includes(record.name)) contract.names.push(record.name);
    } else {
      contracts.set(key, { address, chain, names: [record.name] });
    }
  }
  for (const contract of contracts.values()) {
    specs.push({
      label: `SMART CONTRACT · ${contract.names.join(' / ')}${contract.chain ? ` (${contract.chain.toUpperCase()})` : ''}`,
      specifications: [{ value: contract.address, light: true }],
    });
  }
  if (detail.requiredInformation?.collectionInscriptionAddress) {
    specs.push({
      label: 'INSCRIPTION ADDRESS',
      specifications: [{ value: detail.requiredInformation.collectionInscriptionAddress, light: true }],
    });
  }
  const platforms = new Set<string>();
  const parentContractRepeated = Boolean(detail.requiredInformation?.contractAddress) && records.some(
    child => child.id !== detail.id && contractKey(child) === contractKey(detail)
  );
  const platformRecords = parentContractRepeated ? records.filter(record => record.id !== detail.id) : records;
  for (const record of platformRecords) {
    const platform = record.requiredInformation?.datamarket;
    if (!platform) continue;
    const key = `${record.name}:${platform}:${record.marketUrl ?? ''}`;
    if (platforms.has(key)) continue;
    platforms.add(key);
    specs.push({
      label: `PLATFORM · ${record.name}`,
      specifications: [
        record.marketUrl
          ? { value: platform, url: record.marketUrl, light: true }
          : { value: platform, light: true },
      ],
    });
  }
  return specs;
}

export function CollectionBlock({ layout, specs = [] }: { layout: Block; specs?: ReturnType<typeof buildCollectionSpecs> }) {
  switch (layout.blockType) {
    case 'LandscapeDisplay':
      return <Landscape_Display layout={layout} />;
    case 'LandscapeImmersive':
      return <Landscape_Immersive layout={layout} />;
    case 'LandscapeVisionDisplay':
      return <Landscape_Vision_Display layout={layout} />;
    case 'LandscapeChapterHero':
      return <Landscape_ChapterHero layout={layout} />;
    case 'LandscapeChapter':
      return <Landscape_Chapter layout={layout} />;
    case 'LandscapeProcess':
      return <Landscape_Process layout={layout} />;
    case 'LandscapeSketchbook':
      return <Landscape_Sketchbook layout={layout} />;
    case 'LandscapeDescription':
      return <Landscape_Description layout={layout} specs={specs} />;
    default:
      return null;
  }
}

export default function CollectionBlocks({ detail, blocksOverride, relatedCollections = [] }: { detail: CollectionWithExtras; blocksOverride?: Block[]; relatedCollections?: CollectionWithExtras[] }) {
  const blocks: Block[] = blocksOverride ?? (detail.layout?.length ? detail.layout : detail.description ? [{ blockType: 'LandscapeDescription', title: 'Synopsis', description: detail.description, column: 2 }] : []);
  const descriptionIndex = blocks.findIndex(block => block.blockType === 'LandscapeDescription');
  const specs = buildCollectionSpecs(detail, relatedCollections);
  return (
    <>
      {groupVideoDisplays(blocks).map(group =>
        group.length > 1 ? (
          // Display videos placed next to each other in the CMS play side by side,
          // as a multi-screen work is shown. The block name is the screen's caption.
          <div key={group[0].index} className="grid grid-cols-1 md:grid-cols-2 gap-x-[12px]" data-video-row>
            {group.map(({ block, index }) => (
              <figure key={index}>
                <CollectionBlock layout={block} />
                {block.blockName ? (
                  <figcaption className="mb-[12px] text-[9px] uppercase tracking-wider">{block.blockName}</figcaption>
                ) : null}
              </figure>
            ))}
          </div>
        ) : (
          <CollectionBlock key={group[0].index} layout={group[0].block} specs={group[0].index === descriptionIndex ? specs : undefined} />
        )
      )}
    </>
  );
}

/** Runs of adjacent LandscapeDisplay blocks that carry a video become one group. */
function groupVideoDisplays(blocks: Block[]) {
  const groups: Array<Array<{ block: Block; index: number }>> = [];
  let previousWasVideo = false;
  blocks.forEach((block, index) => {
    const isVideo = block.blockType === 'LandscapeDisplay' && Boolean(block.video);
    if (isVideo && previousWasVideo) groups[groups.length - 1].push({ block, index });
    else groups.push([{ block, index }]);
    previousWasVideo = isVideo;
  });
  return groups;
}
